<!--
    DiseasesCategorizedField — the PATIENT side of the 5th Open Dental data type
    ("DISEASES with Category and Checkbox Options", control.dataType = 'DISEASES_CATEGORIZED').

    WHAT THE PATIENT SEES
    Big category tiles (Heart or Circulatory, Lung or Breathing, …) plus an exclusive
    "No conditions" tile. Tapping a tile reveals that category's checkbox grid. This replaces the
    type-ahead `vue-multiselect` that elderly patients could not work — it is the SAME stored value,
    only a different way of picking it.

    WHAT IT STORES — do NOT change the shape
    `[{ text, value, category? }]`, `value` being an Open Dental `DiseaseDefNum`. The `category` tag
    is additive (the backend ignores it; it exists so the submission view and the PDF can group).
    "No conditions" stores the None sentinel `[{ text: 'None', value: 'none' }]`. Every rule about
    dedupe / preserved problems / the None sentinel lives in the pure library
    `libraries/od-categorized-diseases.js`; this component is wiring and markup ONLY, so the rules
    stay testable in node and cannot drift between the builder and the patient form.

    WHAT IT MUST NOT DO
    - Never render the control's LABEL: `views/renderer/ControlView.vue` already renders it.
    - Never mutate `control` (it is the saved builder config, shared with the sidebar).
    - `viewSubmissionMode` is the staff submission view AND the Puppeteer-rendered PDF: plain text
      only, never an input.
-->
<template>
    <div class="odc-field">
        <!-- Staff submission view + PDF: grouped plain text, no inputs. Empty value → nothing. -->
        <template v-if="viewSubmissionMode">
            <div v-if="displayGroups.length > 0" class="odc-summary">
                <p v-for="group in displayGroups" :key="group.id" class="odc-summary__group">
                    <template v-if="group.id === noneGroupId">{{ group.title }}</template>
                    <template v-else>
                        <strong>{{ groupTitle(group) }}:</strong> {{ groupItems(group) }}
                    </template>
                </p>
            </div>
        </template>

        <!--
            Nothing configured yet. In the builder the office needs to be told where to set it up;
            on a live form it renders nothing at all, exactly like the legacy control with no
            selectedOptions.
        -->
        <template v-else-if="!hasSetup">
            <p v-if="builderMode" class="odc-hint">
                Add categories and conditions in Control Specific Configuration.
            </p>
        </template>

        <template v-else>
            <div class="odc-tiles">
                <label
                    v-for="cat in tileCategories"
                    :key="cat.id"
                    class="odc-tile"
                    :class="{ 'is-selected': isOpen(cat) }"
                >
                    <input
                        class="odc-tile__input"
                        type="checkbox"
                        :checked="isOpen(cat)"
                        @change="onToggleCategory(cat)"
                    >
                    <span class="odc-tile__check" aria-hidden="true">&#10003;</span>
                    <span class="odc-tile__text">{{ categoryName(cat) }}</span>
                </label>

                <label
                    v-if="showNone"
                    class="odc-tile odc-tile--none"
                    :class="{ 'is-selected': state.noConditions }"
                >
                    <input
                        class="odc-tile__input"
                        type="checkbox"
                        :checked="state.noConditions"
                        @change="onNoConditions"
                    >
                    <span class="odc-tile__check" aria-hidden="true">&#10003;</span>
                    <span class="odc-tile__text">{{ noneLabel }}</span>
                </label>
            </div>

            <div
                v-for="cat in openSections"
                :key="cat.id"
                class="odc-section"
                :data-category-id="cat.id"
                role="group"
                :aria-label="categoryName(cat)"
            >
                <!--
                    A category holding only Open Dental's "Other" (the default "Other Conditions") IS
                    the answer "Other": no heading, no checkboxes — just the description box below.
                -->
                <template v-if="!isOtherOnly(cat)">
                    <p class="odc-section__title">
                        <strong>{{ categoryName(cat) }}</strong>: {{ t('whichApply') }}
                    </p>

                    <!-- `.radio-checkbox` so the existing form checkbox CSS applies unchanged. -->
                    <div class="odc-options radio-checkbox">
                        <label
                            v-for="opt in checkboxOptions(cat)"
                            :key="'opt-' + String(opt.value)"
                            class="odc-option"
                        >
                            <!-- Disease names come from Open Dental and are never translated. -->
                            <input
                                type="checkbox"
                                :checked="isTicked(cat, opt)"
                                @change="onTick(cat, opt, $event.target.checked)"
                            >
                            <span>{{ opt.text }}</span>
                        </label>
                    </div>
                </template>

                <!--
                    Open Dental's "Other" is never a checkbox — it is answered here, and the text is
                    saved as the note on the patient's Other problem (Open Dental's "Other Problems").
                    REQUIRED under an "Other"-only category (its tile is the answer); OPTIONAL among
                    other conditions, where typing selects "Other" and leaving it blank does not (the
                    library's syncOtherNote). Outside every checkbox <label>, so a tap in the box never
                    toggles anything. Shown under the first open category holding "Other" only.
                -->
                <div
                    v-if="cat.id === otherNoteCategoryId"
                    class="odc-other-note"
                    :class="{ 'is-blank': state.otherNote.trim() === '', 'is-required': otherNoteRequired, 'is-alone': isOtherOnly(cat) }"
                >
                    <label class="odc-other-note__label" :for="otherNoteInputId">{{ otherNoteRequired ? t('describeOther') : t('describeOtherOptional') }}</label>
                    <textarea
                        :id="otherNoteInputId"
                        class="odc-other-note__input"
                        rows="3"
                        :maxlength="otherNoteMaxLength"
                        :placeholder="t('describeOtherPlaceholder')"
                        :aria-required="otherNoteRequired ? 'true' : 'false'"
                        :value="state.otherNote"
                        @input="onOtherNote($event.target.value)"
                    ></textarea>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import {
        DEFAULT_NONE_LABEL,
        NONE_GROUP_ID,
        PRESERVED_GROUP_ID,
        OTHER_NOTE_MAX_LENGTH,
        emptyState,
        initStateFromValue,
        toggleCategoryOpen,
        setTick,
        setNoConditions,
        setOtherNote,
        syncOtherNote,
        otherNoteCategoryId as findOtherNoteCategoryId,
        isOtherNoteRequired,
        isOtherOption,
        isOtherOnlyCategory,
        projectState,
        groupForDisplay
    } from "../../libraries/od-categorized-diseases";
    import { getLocalizedField } from "../../utils/languageHelper";
    import { t as systemTranslation } from "../../utils/systemTranslations";

    export default {
        name: "DiseasesCategorizedField",

        props: {
            control: {
                type: Object,
                required: true
            },
            // v-model value: the stored [{text, value, category?}] array (any type defensively).
            value: {
                default: null
            },
            language: {
                type: String,
                default: 'en'
            },
            viewSubmissionMode: {
                type: Boolean,
                default: false
            },
            builderMode: {
                type: Boolean,
                default: false
            }
        },

        data: () => ({
            state: emptyState(),
            // JSON of the last array this component emitted, so the value echoing back through the
            // parent does not re-derive (and collapse) the open/closed tiles the patient just set.
            lastEmitted: null
        }),

        watch: {
            /**
             * `immediate` so a value ALREADY PRESENT at mount renders ticked. An OD control inside a
             * conditionally-visible section mounts after its data loads, so the value never
             * "changes" and a non-immediate watcher never fires — same mount-timing bug class as the
             * DatePicker "birth date not staying" fix (2026-07-01).
             */
            value: {
                immediate: true,
                handler(val) {
                    const json = JSON.stringify(Array.isArray(val) ? val : null);
                    if (json === this.lastEmitted) return;
                    this.state = initStateFromValue(this.control, val);
                }
            }
        },

        computed: {
            /**
             * The configured categories, EXACTLY as the builder persisted them.
             * Deliberately NOT the library's normalize-categories helper: that assigns a fresh id
             * to every id-less row on every call, so the tiles would change identity on each
             * re-render. The builder persists the ids, so they are taken as given here.
             *
             * @returns {Array<Object>}
             */
            categories() {
                return Array.isArray(this.control.categories)
                    ? this.control.categories.filter(cat => cat && cat.id)
                    : [];
            },

            /**
             * The categories a PATIENT gets a tile for: only the ones with something to tick.
             * The six defaults ship with empty lists and an office may fill just a few of them,
             * so an empty category must never become a tile that opens nothing.
             * @returns {Array<Object>}
             */
            tileCategories() {
                return this.categories.filter(cat => Array.isArray(cat.options) && cat.options.length > 0);
            },

            /**
             * Open categories, in configuration order.
             * @returns {Array<Object>}
             */
            openCategories() {
                return this.categories.filter(cat => this.isOpen(cat));
            },

            /**
             * Open categories that actually have something to tick — an open but empty category
             * renders no section at all.
             * @returns {Array<Object>}
             */
            openSections() {
                return this.openCategories.filter(cat => Array.isArray(cat.options) && cat.options.length > 0);
            },

            /**
             * Is the exclusive "No conditions" tile offered? Absent ⇒ ON (the office opts out).
             * @returns {boolean}
             */
            showNone() {
                return this.control.showNoneChoice !== false;
            },

            /**
             * Caption of the "No conditions" tile: the office's own wording (per language) first,
             * then the translated default, then the library constant.
             * @returns {string}
             */
            noneLabel() {
                return getLocalizedField(this.control, 'noneLabel', this.language)
                    || this.t('noConditions')
                    || DEFAULT_NONE_LABEL;
            },

            /**
             * Has the office set this control up at all (≥ 1 category holding ≥ 1 disease)?
             * @returns {boolean}
             */
            hasSetup() {
                return this.categories.some(cat => Array.isArray(cat.options) && cat.options.length > 0);
            },

            /**
             * Submission-view groups (see libraries/od-categorized-diseases.js groupForDisplay).
             * @returns {Array<{id: string, title: string, items: Array}>}
             */
            displayGroups() {
                if (!this.viewSubmissionMode) return [];
                return groupForDisplay(this.control, this.value, {
                    noneLabel: this.noneLabel,
                    alsoOnFileLabel: this.t('alsoOnFile')
                });
            },

            /** Template access to the synthetic "No conditions" group id. */
            noneGroupId() {
                return NONE_GROUP_ID;
            },

            /**
             * The category the "Other" description box shows under ('' = no ticked "Other").
             * @returns {string}
             */
            otherNoteCategoryId() {
                return findOtherNoteCategoryId(this.control, this.state);
            },

            /**
             * Is the showing description box required? Only under an "Other"-only category.
             * @returns {boolean}
             */
            otherNoteRequired() {
                return isOtherNoteRequired(this.control, this.state);
            },

            /** A per-control id so the box's <label for> works with several controls on one form. */
            otherNoteInputId() {
                return 'odc-other-note-' + String(this.control.uniqueId || this.control.name || 'field');
            },

            otherNoteMaxLength() {
                return OTHER_NOTE_MAX_LENGTH;
            }
        },

        methods: {
            /**
             * A fixed UI string in the patient's language (disease names are never translated).
             *
             * @param {string} key
             * @returns {string}
             */
            t(key) {
                return systemTranslation(key, this.language);
            },

            /**
             * The category's name in the patient's language.
             *
             * @param {Object} cat
             * @returns {string}
             */
            categoryName(cat) {
                return getLocalizedField(cat, 'name', this.language) || cat.name;
            },

            /**
             * @param {Object} cat
             * @returns {boolean}
             */
            isOpen(cat) {
                return this.state.openCategoryIds.indexOf(cat.id) !== -1;
            },

            /**
             * @param {Object} cat
             * @param {{value: *}} opt
             * @returns {boolean}
             */
            isTicked(cat, opt) {
                const ticks = this.state.ticked[cat.id];
                return Array.isArray(ticks) && ticks.indexOf(String(opt.value)) !== -1;
            },

            /**
             * Heading of a submission-view group: the None/"Also on file" groups already carry a
             * translated title; a category group is titled from the LIVE (localized) category name.
             *
             * @param {{id: string, title: string}} group
             * @returns {string}
             */
            groupTitle(group) {
                if (group.id === NONE_GROUP_ID || group.id === PRESERVED_GROUP_ID) return group.title;
                const cat = this.categories.filter(c => c.id === group.id)[0];
                return cat ? this.categoryName(cat) : group.title;
            },

            /**
             * A described "Other" reads "Other (their description)" — plain text for the staff
             * view and the PDF.
             *
             * @param {{items: Array<{text: *, note?: string}>}} group
             * @returns {string}
             */
            groupItems(group) {
                return group.items.map(item => (item.note ? `${item.text} (${item.note})` : item.text)).join(', ');
            },

            /**
             * Does this category hold nothing but Open Dental's "Other"? Its tile is then the answer.
             *
             * @param {Object} cat
             * @returns {boolean}
             */
            isOtherOnly(cat) {
                return isOtherOnlyCategory(cat);
            },

            /**
             * A category's conditions as checkboxes — everything except "Other", which is answered
             * through the description box.
             *
             * @param {Object} cat
             * @returns {Array<Object>}
             */
            checkboxOptions(cat) {
                return (Array.isArray(cat.options) ? cat.options : []).filter(opt => !isOtherOption(opt));
            },

            /**
             * Adopt a new UI state and emit the value it projects to. syncOtherNote first: it ticks or
             * unticks "Other" from the box (the tile / the typed text) and drops text the patient can
             * no longer see, so it is never saved.
             *
             * @param {Object} nextState
             */
            commit(nextState) {
                this.state = syncOtherNote(this.control, nextState);
                const out = projectState(this.control, this.state);
                this.lastEmitted = JSON.stringify(out);
                this.$emit('change', out);
            },

            /**
             * Typing in the "Other" description box.
             *
             * @param {string} text
             */
            onOtherNote(text) {
                this.commit(setOtherNote(this.state, text));
            },

            /**
             * Tap a category tile. Closing a category drops its ticks (the library's rule).
             *
             * @param {Object} cat
             */
            onToggleCategory(cat) {
                this.commit(toggleCategoryOpen(this.state, cat.id));
            },

            /**
             * Tick / untick one condition.
             *
             * @param {Object} cat
             * @param {{value: *}} opt
             * @param {boolean} checked
             */
            onTick(cat, opt, checked) {
                this.commit(setTick(this.state, cat.id, opt.value, checked));
            },

            /**
             * Tap "No conditions". Tapping it AGAIN unticks it back to a blank answer, so a patient
             * who taps it by accident is not stuck with it (the tile is a real checkbox and a
             * checkbox you cannot untick is a trap); `required` then fails as it should.
             */
            onNoConditions() {
                if (this.state.noConditions) {
                    this.commit(emptyState());
                    return;
                }
                this.commit(setNoConditions(this.state));
            }
        }
    }
</script>

<style lang="scss" scoped>
    $odc-accent: #0f7259;      // Teamio teal — the patient-form accent (PacketProgressStrip)
    $odc-border: #cbd5e1;
    $odc-text: #334155;
    $odc-muted: #64748b;

    .odc-field {
        font-size: 16px;
    }

    /* ---------------------------------------------------------------------
       Category tiles — 1 column on phones, 2 from the sm breakpoint up.
       --------------------------------------------------------------------- */
    .odc-tiles {
        display: grid;
        grid-template-columns: 1fr;
        gap: 10px;

        @media (min-width: 576px) {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    .odc-tile {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 0;
        min-height: 48px;
        padding: 12px 14px;
        font-size: 16px;
        font-weight: 600;
        line-height: 1.3;
        color: $odc-text;
        background-color: #ffffff;
        border: 2px solid $odc-border;
        border-radius: 8px;
        cursor: pointer;

        /*
         * `!important` + the extra class on purpose. ViewSubmissionForPDF.vue ships an UNSCOPED
         * stylesheet (pages are not lazy-loaded, so it is live on every form renderer, including
         * the patient's ViewForm) that forces `.vue-form-renderer * { color: #000 !important }`
         * and `.vue-form-renderer .control-view span { color: #000 !important; opacity: 1
         * !important }` for PDF legibility. Without this, the selected tile rendered BLACK text
         * on teal (seen in the 2026-09-17 Playwright screenshots). These selectors out-rank it.
         */
        &.is-selected,
        &.is-selected .odc-tile__text,
        &.is-selected .odc-tile__check {
            color: #ffffff !important;
        }

        &.is-selected {
            background-color: $odc-accent;
            border-color: $odc-accent;
        }

        /* The real checkbox stays in the DOM (keyboard + screen readers); only hidden visually. */
        &:focus-within {
            outline: 2px solid $odc-accent;
            outline-offset: 2px;
        }
    }

    .odc-tile__input {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: 0;
        overflow: hidden;
        white-space: nowrap;
        border: 0;
        clip: rect(0 0 0 0);
        clip-path: inset(50%);
    }

    .odc-tile__check {
        flex: 0 0 auto;
        width: 20px;
        font-size: 18px;
        line-height: 1;
        text-align: center;
        /* `visibility`, NOT `opacity`: the unscoped PDF stylesheet forces `opacity: 1 !important`
           on every span in a form renderer, which left a check mark on UNSELECTED tiles. */
        visibility: hidden;

        .odc-tile.is-selected & {
            visibility: visible;
        }
    }

    .odc-tile__text {
        flex: 1 1 auto;
    }

    /* ---------------------------------------------------------------------
       One section per open category.
       --------------------------------------------------------------------- */
    .odc-section {
        margin-top: 18px;
    }

    .odc-section__title {
        margin-bottom: 8px;
        font-size: 16px;
        line-height: 1.35;
        color: $odc-text;
    }

    .odc-options {
        display: grid;
        grid-template-columns: 1fr;
        gap: 4px 18px;

        @media (min-width: 576px) {
            grid-template-columns: repeat(2, 1fr);
        }

        @media (min-width: 768px) {
            grid-template-columns: repeat(3, 1fr);
        }
    }

    .odc-option {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        margin: 0;
        min-height: 32px;
        padding: 4px 0;
        font-size: 16px;
        line-height: 1.35;
        color: $odc-text;
        cursor: pointer;

        input {
            flex: 0 0 auto;
            width: 18px;
            height: 18px;
            margin-top: 3px;
            accent-color: $odc-accent;
        }
    }

    /* ---------------------------------------------------------------------
       The "Other" description box — under the category's checkboxes, or on its own when the
       category holds only "Other" (its tile is the answer).
       --------------------------------------------------------------------- */
    .odc-other-note {
        margin: 12px 0 4px;

        &.is-alone {
            margin-top: 0;
        }
    }

    .odc-other-note__label {
        display: block;
        margin-bottom: 6px;
        font-size: 16px;
        font-weight: 600;
        color: $odc-text;
    }

    .odc-other-note__input {
        display: block;
        width: 100%;
        min-height: 88px;
        padding: 10px 12px;
        /* 16px, never rem (the app root is 14px): iOS Safari zooms in when a field under 16px is
           tapped. Pinned by cypress/node-tests/ios-focus-zoom-guardrail.test.js. */
        font-size: 16px;
        line-height: 1.4;
        color: $odc-text;
        background-color: #ffffff;
        border: 1px solid $odc-border;
        border-radius: 8px;
        resize: vertical;

        &:focus {
            outline: none;
            border-color: $odc-accent;
            box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12);
        }

        /* Muted like every other patient-form placeholder (ViewForm's `.form-control::placeholder`,
           which this textarea is not) — near-black placeholder text reads as already typed. */
        &::placeholder {
            color: #94a3b8 !important;
            opacity: 1;
        }
    }

    /* ControlView marks the component root invalid when validation fails; only a BLANK REQUIRED box
       is the culprit (the required-description rule), so only it turns red — an optional box never
       does, even when the control fails its own Required rule. `!important` because ViewForm's
       patient-form `textarea { border: … !important }` would otherwise win; this selector out-ranks
       it on specificity. */
    .is-invalid .odc-other-note.is-required.is-blank .odc-other-note__input,
    .md-error .odc-other-note.is-required.is-blank .odc-other-note__input {
        border-color: #dc2626 !important;
    }

    /* ---------------------------------------------------------------------
       Builder hint + submission view.
       --------------------------------------------------------------------- */
    .odc-hint {
        margin: 0;
        font-size: 13px;
        font-style: italic;
        color: $odc-muted;
    }

    .odc-summary__group {
        margin: 0 0 4px;
        line-height: 1.4;
    }
</style>
